import { defineStore } from 'pinia';
import { ref } from 'vue';
import { fetchStartDayCodes, fetchMachines, fetchObservationTypes, fetchPBRs, fetchSpecies, fetchStrains, fetchEnsemblMetadata, fetchAlgaebaseMetadata, fetchGeoMetadata, fetchWikimediaMetadata } from '../functions/fetch_tables';
import { fetchThresholds } from '@/functions/functions_pilotage_api';

export function compareNumeroPbr(a: string, b: string): number {
    const re = /^([a-zA-Z]+)(\d+)$/;

    const matchA = a.match(re);
    const matchB = b.match(re);

    if (!matchA || !matchB) {
        return a.localeCompare(b);
    }

    const [, prefixA, numA] = matchA;
    const [, prefixB, numB] = matchB;

    if (prefixA !== prefixB) {
        return prefixA.localeCompare(prefixB);
    }

    return parseInt(numA, 10) - parseInt(numB, 10);
}

export const useDataStore = defineStore('dataStore', () => {
    const showCreateCulture = ref(false);

    const newCulture = ref({
        id: 0,
        previous_ip_code: '',
        inoculum_code: '',
        inoculum_do: 0,
        pbr: { id: 0, numero_pbr: '' },
        specie: { id: 0, specie_code: '', specie_name: '' },
        strain: {
            id: 0,
            code: '',
            origin: '',
            sub_code: '',
            code_order: '',
            domain: '',
            kingdom: '',
            phylum: '',
            specie_class: '',
            superorder: '',
            specie_order: '',
            family: '',
            genus: '',
            species: '',
            reception_date: null,
            collection: '',
            lot: '',
            strain_origin: '',
            isolation_year: null,
        },
        startdaycode: { id: 0, startday_code: '' },
        active: false,
        prelevements: [],
        recoltes: [],
        analyses: [],
    });

    const newRecolte = ref<{
        daycode: string;
        machine_id: number | null;
        culture_ids: number[];
        specie_id: number | null;
        volume: number;
        batches: {
            batch_code: string;
            quantity: number;
            dry_weight: number;
            concentration_fcx: number;
        }[];
    }>({
        daycode: '',
        machine_id: null,
        culture_ids: [],
        specie_id: null,
        volume: 0,
        batches: [],
    });

    const isEditing = ref(false);
    const editId = ref<number | null>(null);
    const selectedRecolteForBatch = ref<Recolte | null>(null);
    const selectedRecolteForEdit = ref<Recolte | null>(null);
    const selectedCulture = ref<Culture | null>(null);

    const selectedCultures: number[] = [];

    const cultures = ref<Culture[]>([]);
    const detailedCultures = ref<Record<number, Culture>>({});
    const prelevements = ref<Prelevement[]>([]);
    const pbrs = ref<PBR[]>([]);
    const species = ref<Species[]>([]);
    const strains = ref<Strain[]>([]);
    const startdaycodes = ref<StartDayCode[]>([]);
    const machines = ref<Machine[]>([]);
    const recoltes = ref<Recolte[]>([]);
    const thresholds = ref<Record<number, (number | null)[]>>({});
    const localThresholds = ref<(number | null)[]>(Array(7).fill(null));
    const manualValues = ref<Record<number, ManualValue>>({});
    const latestManualUpdate = ref<Date | null>(null);
    const commentaire = ref<Commentaire | null>(null);
    const analyses = ref<Analyse[]>([]);


    const prelevement = ref<Prelevement>({
        id: 0,
        culture_id: 0,
        daycode: '',
        user: null,
        commentaires: null,
        absorbance_880nm: null,
        vol_recolte: null,
        ph_lu: null,
        ph_mesure: null,
        vol_traite: 0,
        masse_residuelle: null,
        temperature: null,
        observation_values: [],
    });

    const observationTypes = ref<ObservationType[]>([]);

    const enrichedCulture = ref<Culture & {
        age_in_days: number;
        number_of_harvests: number;
        number_of_samples: number;
    } | null>(null);

    const calculationInputAbsorbance = ref('');
    const calculationInputVolumeTraite = ref('');
    const showInactive = ref(false);
    const showCreateRecolte = ref(false);
    const newRecolteDaycode = ref('');
    const newRecolteMachineId = ref<number | null>(null);
    const creationError = ref('');
    const creationSuccess = ref('');
    const newBatch = ref({
        batch_code: '',
        quantity: null,
        rec: false
    });
    const expandedRecoltes = ref(new Set());
    const showEditRecolte = ref(false);
    const formErrorMessage = ref('');
    const filtreEspece = ref<number | null>(null);
    const filtreAnnee = ref<number | null>(null);
    const filtreMois = ref<number | null>(null);
    const filtrePBR = ref<number | null>(null);

    const filtreEspece_setting = ref<number | null>(null);
    const filtrePBR_setting = ref<number | null>(null);

    const isPrelevementsLoading = ref(false);
    const isDataLoaded = ref(false);
    const isDataLoading = ref(false);
    const isPilotageLoading = ref(false);

    const isBasicDataLoaded = ref(false);
    const isBasicDataLoading = ref(false);

    // Metadata
    const ensemblMetadata = ref<EnsemblSpeciesMetadata[]>([]);
    const algaebaseMetadata = ref<AlgaebaseSpeciesMetadata[]>([]);
    const geoMetadata = ref<GeoSpeciesMetadata[]>([]);
    const wikimediaMetadata = ref<WikimediaSpeciesMetadata[]>([]);

    async function loadBasicData() {
        // Ne relance pas si déjà chargé
        if (isBasicDataLoaded.value) return;

        isBasicDataLoading.value = true;

        await fetchPBRs();
        await fetchSpecies();
        await fetchStrains();
        await fetchStartDayCodes();
        await fetchMachines();
        await fetchObservationTypes();
        await fetchThresholds();
        await initializeLocalThreshold();

        isBasicDataLoading.value = false;
        isBasicDataLoaded.value = true;

    }

    async function loadEnsemblForStrain(strainId: number) {
        try {
            ensemblMetadata.value = await fetchEnsemblMetadata(strainId);
        } catch (error) {
            console.error('Erreur lors du chargement des métadonnées Ensembl:', error);
            ensemblMetadata.value = [];
        }
    }

    async function loadAlgaebaseForStrain(strainId: number) {
        try {
            algaebaseMetadata.value = await fetchAlgaebaseMetadata(strainId);
        } catch (error) {
            console.error('Erreur lors du chargement des métadonnées AlgaeBase:', error);
            algaebaseMetadata.value = [];
        }
    }

    async function loadGeoForStrain(strainId: number) {
        try {
            geoMetadata.value = await fetchGeoMetadata(strainId);
        } catch (error) {
            console.error('Erreur lors du chargement des métadonnées GEO', error);
            geoMetadata.value = [];
        }
    }

    async function loadWikimediaForStrain(strainId: number) {
        try {
            wikimediaMetadata.value = await fetchWikimediaMetadata(strainId);
        } catch (error) {
            console.error('Erreur lors du chargement des métadonnées Wikimedia:', error);
            wikimediaMetadata.value = [];
        }
    }

    function getAnalysesByRelation(
        relation: 'specie',
        id: number
    ) {
        if (!id) return [];

        const keyMap = {
            specie: 'specie',
        } as const;

        const key = keyMap[relation];
        return analyses.value.filter(a => a[key]?.id === id);
    }

    function getConcentration(analyse: any): string {
        const {
            aire_echantillon_dilue,
            ge_value,
            preparation_echantillon_masse_pesee,
            preparation_echantillon_volume_total
        } = analyse;

        const vals = [
            aire_echantillon_dilue,
            ge_value,
            preparation_echantillon_masse_pesee,
            preparation_echantillon_volume_total
        ];

        const allValid = vals.every(v => typeof v === 'number' && !isNaN(v)) &&
            ge_value !== 0 &&
            preparation_echantillon_volume_total !== 0 &&
            preparation_echantillon_masse_pesee !== 0;

        if (allValid) {
            const ratioDilution = aire_echantillon_dilue / ge_value;
            const masseEnKg = preparation_echantillon_masse_pesee / 1000;
            const concentrationFactor = 1 / (masseEnKg / preparation_echantillon_volume_total);
            const finalValue = ratioDilution * concentrationFactor;

            return finalValue.toFixed(2);
        }

        return '';
    }

    function getMostRecentAnalyseWithFx(analyses: Analyse[] = []): Analyse | null {
        if (!analyses.length) return null;
        // Trie décroissant sur daycode pour avoir la plus récente
        return analyses.sort((a, b) => b.daycode.localeCompare(a.daycode))[0];
    }

    function setThreshold(specieId: number, values: (number | null)[]) {
        if (values.length !== 7) {
            console.error(`Le tableau des seuils doit contenir exactement 7 valeurs`);
            return;
        }
        thresholds.value[specieId] = [...values]; // Créer une copie pour garantir la réactivité
    }

    function initializeLocalThreshold() {
      const specieId = filtreEspece_setting.value;
      if (specieId !== null && thresholds.value[specieId]) {
        localThresholds.value = [...thresholds.value[specieId]];
      } else {
        localThresholds.value = Array(7).fill(null) as (number | null)[];
      }
    }

    return {
        showCreateCulture,
        newCulture,
        newRecolte,
        prelevements,
        observationTypes,
        prelevement,
        isEditing,
        editId,
        selectedRecolteForBatch,
        selectedRecolteForEdit,
        selectedCulture,
        selectedCultures,
        cultures,
        detailedCultures,
        pbrs,
        species,
        strains,
        startdaycodes,
        machines,
        recoltes,
        enrichedCulture,
        calculationInputVolumeTraite,
        calculationInputAbsorbance,
        showInactive,
        showCreateRecolte,
        newRecolteDaycode,
        newRecolteMachineId,
        creationError,
        creationSuccess,
        newBatch,
        expandedRecoltes,
        showEditRecolte,
        formErrorMessage,
        filtreEspece,
        filtreAnnee,
        filtreMois,
        filtrePBR,
        filtreEspece_setting,
        filtrePBR_setting,
        isDataLoading,
        isDataLoaded,
        isPrelevementsLoading,
        isPilotageLoading,
        isBasicDataLoaded,
        loadBasicData,
        thresholds,
        localThresholds,
        setThreshold,
        manualValues,
        latestManualUpdate,
        commentaire,
        getConcentration,
        getMostRecentAnalyseWithFx,
        analyses,
        getAnalysesByRelation,
        ensemblMetadata,
        loadEnsemblForStrain,
        algaebaseMetadata,
        loadAlgaebaseForStrain,
        geoMetadata,
        loadGeoForStrain,
        wikimediaMetadata,
        loadWikimediaForStrain
    };
});